"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { errorMessage } from "@/lib/api";

export type ToastKind = "success" | "error" | "warning" | "info";

export type ToastAction = {
  label: string;
  href: string;
};

export type ToastOptions = {
  /** Override auto-dismiss in ms. Pass 0 to require a manual dismiss. */
  durationMs?: number;
  /** Optional shorter label rendered before the message (e.g. "Skill installed"). */
  title?: string;
  /** Optional inline link rendered alongside the message (e.g. "Open Settings → Usage"). */
  action?: ToastAction;
  /** Render a spinner instead of the kind icon (for in-progress work). Pair
   *  with `durationMs: 0` and dismiss explicitly when the work completes. */
  loading?: boolean;
};

type Toast = {
  id: number;
  kind: ToastKind;
  title?: string;
  message: string;
  durationMs: number;
  action?: ToastAction;
  loading?: boolean;
};

type ToastContextValue = {
  show: (kind: ToastKind, message: string, opts?: ToastOptions) => number;
  success: (message: string, opts?: ToastOptions) => number;
  error: (message: string, opts?: ToastOptions) => number;
  warning: (message: string, opts?: ToastOptions) => number;
  info: (message: string, opts?: ToastOptions) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION: Record<ToastKind, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 7000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (kind: ToastKind, message: string, opts?: ToastOptions) => {
      const id = nextId.current++;
      const durationMs = opts?.durationMs ?? DEFAULT_DURATION[kind];
      setToasts((cur) => [
        ...cur,
        { id, kind, title: opts?.title, message, durationMs, action: opts?.action, loading: opts?.loading },
      ]);
      return id;
    },
    [],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m, o) => show("success", m, o),
      error: (m, o) => show("error", m, o),
      warning: (m, o) => show("warning", m, o),
      info: (m, o) => show("info", m, o),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/** Surface a caught error as an error toast: `toastError(toast, e, "Save failed")`.
 * Message resolution (ApiError.detail → AuthError → Error.message → fallback)
 * lives in `errorMessage`, next to the error classes in lib/api. */
export function toastError(
  toast: Pick<ToastContextValue, "error">,
  err: unknown,
  fallback: string,
  opts?: ToastOptions,
): number {
  return toast.error(errorMessage(err, fallback), opts);
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Defensive fallback: components mounted outside the provider (e.g.
    // unit tests, storybook) get a no-op shape so they don't crash. We
    // log so the gap is obvious in dev without forcing every caller to
    // null-check the hook.
    if (typeof console !== "undefined") {
      console.warn("[toast] useToast() used outside <ToastProvider>; toasts will not render");
    }
    const noShow = () => 0;
    return {
      show: noShow,
      success: noShow,
      error: noShow,
      warning: noShow,
      info: noShow,
      dismiss: () => {},
    };
  }
  return ctx;
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

const KIND_STYLES: Record<ToastKind, { accent: string; iconBg: string; icon: string; role: "status" | "alert" }> = {
  success: {
    accent: "border-emerald-400/40 dark:border-emerald-500/30",
    iconBg: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    icon: "✓",
    role: "status",
  },
  info: {
    accent: "border-border",
    iconBg: "bg-muted text-foreground",
    icon: "i",
    role: "status",
  },
  warning: {
    accent: "border-amber-400/40 dark:border-amber-500/30",
    iconBg: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    icon: "!",
    role: "alert",
  },
  error: {
    accent: "border-red-400/40 dark:border-red-500/30",
    iconBg: "bg-red-500/15 text-red-700 dark:text-red-300",
    icon: "!",
    role: "alert",
  },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const styles = KIND_STYLES[toast.kind];

  useEffect(() => {
    if (toast.durationMs <= 0) return;
    const handle = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(handle);
  }, [toast.id, toast.durationMs, onDismiss]);

  return (
    <div
      role={styles.role}
      className={`pointer-events-auto animate-slide-up rounded-lg border ${styles.accent} bg-card shadow-lg flex items-start gap-3 px-3 py-2.5`}
    >
      {toast.loading ? (
        <svg
          className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-subtle"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-25" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : (
        <div
          className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${styles.iconBg}`}
          aria-hidden="true"
        >
          {styles.icon}
        </div>
      )}
      <div className="min-w-0 flex-1 text-sm leading-snug">
        {toast.title && <div className="font-medium">{toast.title}</div>}
        <div className={toast.title ? "text-subtle" : ""}>{toast.message}</div>
        {toast.action && (
          <a
            href={toast.action.href}
            onClick={() => onDismiss(toast.id)}
            className="mt-1 inline-block text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            {toast.action.label}
          </a>
        )}
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="ml-1 -mr-1 text-subtle hover:text-foreground text-base leading-none"
      >
        ×
      </button>
    </div>
  );
}
